import cn from 'clsx';
import Link from 'next/link';
import {useRouter} from 'next/router';
import PropTypes from 'prop-types';

import useRoutePaths from '@/src/hooks/useRoutePaths';
import TextLocal from '@/src/textLocal/containers/TextLocal';

import {ProgressLine, Text} from '../../../../ui';
import css from '../styles/CreateCourseProgressLine.module.css';

export const STEPS = {
  LANGUAGE: 'language',
  COURSE_ROOT_CREATION: 'courseRootCreation',
  TOPICS_CREATION: 'topicsCreation',
  SUBMIT: 'submit',
};

/**
 * @param {boolean} isStepActive
 * @param {string} text
 * @param {string} isLink
 * @param {string} url
 * @return {JSX.Element}
 */
const getStepItemLabel = (isStepActive, text, isLink, url) => {
  const mainJSX = (
    <div
      className={cn(css.step, {
        [css.activeStep]: isStepActive,
      })}
    >
      <Text
        fontWeight={
          isStepActive ? Text.FONT_WEIGHT.BOLD : Text.FONT_WEIGHT.NORMAL
        }
        fontSize={Text.FONT_SIZE.SIZE_18}
        color={isStepActive ? Text.COLOR.PRIMARY : Text.COLOR.GREY}
        key={text}
      >
        <TextLocal id={text} />
      </Text>
    </div>
  );

  return isLink ? (
    <Link href={url} key={text}>
      <a>{mainJSX}</a>
    </Link>
  ) : (
    <div key={text}>{mainJSX}</div>
  );
};

/**
 * @param {string} currentStep
 * @return {{progress: string, description: string}}
 */
const getStepData = (currentStep) => {
  let progress;
  let description;

  switch (currentStep) {
    case STEPS.LANGUAGE: {
      progress = ProgressLine.PROGRESS.ONE_QUARTER;
      description = 'create-course.step-one';
      break;
    }
    case STEPS.COURSE_ROOT_CREATION: {
      progress = ProgressLine.PROGRESS.TWO_QUARTERS;
      description = 'create-course.step-two';
      break;
    }
    case STEPS.TOPICS_CREATION: {
      progress = ProgressLine.PROGRESS.THIRD_QUARTER;
      description = 'create-course.step-three';
      break;
    }
    case STEPS.SUBMIT: {
      progress = ProgressLine.PROGRESS.FULL;
      description = 'create-course.step-four';
      break;
    }
  }

  return {
    progress,
    description,
  };
};

const CreateCourseProgressLine = ({
  currentStep,
  isLastPageAvailable,
  isSecondStepAvailable,
  isThirdPageAvailable,
}) => {
  const {
    query: {courseId},
  } = useRouter();
  const {
    CREATOR_STUDIO_FIRST_STEP,
    CREATOR_STUDIO_SECOND_STEP,
    CREATOR_STUDIO_THIRD_STEP,
    CREATOR_STUDIO_FOURTH_STEP,
  } = useRoutePaths();

  const stepsData = [
    {
      isActive: currentStep === STEPS.LANGUAGE,
      text: 'create-course.general',
      url: CREATOR_STUDIO_FIRST_STEP.getLink(courseId as string),
      isLink: true,
    },
    {
      isActive: currentStep === STEPS.COURSE_ROOT_CREATION,
      text: 'create-course.create-a-new-course',
      url: CREATOR_STUDIO_SECOND_STEP.getLink(courseId as string),

      isLink: isSecondStepAvailable,
    },
    {
      isActive: currentStep === STEPS.TOPICS_CREATION,
      text: 'create-course.topics-title',
      url: CREATOR_STUDIO_THIRD_STEP.getLink(courseId as string),
      isLink: isThirdPageAvailable,
    },
    {
      isActive: currentStep === STEPS.SUBMIT,
      text: 'create-course.submit',
      url: CREATOR_STUDIO_FOURTH_STEP.getLink(courseId as string),
      isLink: isLastPageAvailable,
    },
  ];

  const {progress, description} = getStepData(currentStep);

  return (
    <div className={css.container}>
      <div className={css.steps}>
        {stepsData.map((stepItem) =>
          getStepItemLabel(
            stepItem.isActive,
            stepItem.text,
            stepItem.isLink,
            stepItem.url
          )
        )}
      </div>
      <ProgressLine progress={progress} />
      <div className={css.description}>
        <Text fontSize={Text.FONT_SIZE.SIZE_14} color={Text.COLOR.GREY}>
          <TextLocal id={description} />
        </Text>
      </div>
    </div>
  );
};

CreateCourseProgressLine.STEPS = STEPS;

CreateCourseProgressLine.propTypes = {
  currentStep: PropTypes.oneOf(Object.values(STEPS)).isRequired,
  isSecondStepAvailable: PropTypes.bool,
  isThirdPageAvailable: PropTypes.bool,
  isLastPageAvailable: PropTypes.bool,
};

CreateCourseProgressLine.defaultProps = {
  isSecondStepAvailable: false,
  isThirdPageAvailable: false,
  isLastPageAvailable: false,
};

export default CreateCourseProgressLine;
